import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useMerchandiseTopBrands from 'hooks/Merchandise/useMerchandiseTopBrands'
import useMerchandiseTopCategories from 'hooks/Merchandise/useMerchandiseTopCategories'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import MerchandiseSearchFormResultItem from './MerchandiseSearchFormResultItem'
import useMerchandiseList from 'hooks/Merchandise/useMerchandiseList'
import Group from 'components/utils/Group'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

const ResultsGrid = styled.div`
  display: grid;

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rem(40)}
  }
`

interface Props {
  text: string
  message?: string
}

function MerchandiseSearchFormNoResults(props: Props) {
  const { text, message } = props
  const intl = useIntl()

  const [brands] = useMerchandiseTopBrands()
  const [categories] = useMerchandiseTopCategories()
  const [list] = useMerchandiseList({
    sortBy: 'recommended',
  })

  const trackingProductConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form No Results', 'product'), { key: 'product' },
  )
  const trackingCategoryConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form No Results', 'category'), { key: 'category' },
  )
  const trackingBrandConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form No Results', 'brand'), { key: 'brand' },
  )

  const defaultMessage = intl.formatMessage(
    {
      id: 'lj2HlLKo+x',
      defaultMessage: "Oops! We don't have any products, brands or categories related to \"{searchText}\" Try a different search term or browse our bestsellers below.",
      description: 'Default message shown when merchandise search returns no results',
    },
    { searchText: text },
  )

  return <Group direction="vertical" gap={20} tabletGap={24}>
    <Caption variant="large">
      {message || defaultMessage}
    </Caption>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: '+k1YP27unS',
          defaultMessage: 'Recommended products',
          description: 'Section title for recommended products in no results view',
        })}
      </Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingProductConfig}>
          {take(list, 3).map((advert, index) => (
            <MerchandiseSearchFormResultItem
              key={advert.id}
              itemId={advert.id}
              type="product"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: 'S/VgYAjAqp',
          defaultMessage: 'Recommended categories',
          description: 'Section title for recommended categories in no results view',
        })}
      </Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingCategoryConfig}>
          {take(categories, 3).map((category, index) => (
            <MerchandiseSearchFormResultItem
              key={category.id}
              itemId={category.id}
              type="category"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: '0alO1CC0h9',
          defaultMessage: 'Recommended brands',
          description: 'Section title for recommended brands in no results view',
        })}
      </Caption>
      <ResultsGrid>
        <OfferListEventsProvider tracking={trackingBrandConfig}>
          {take(brands, 3).map((brand, index) => (
            <MerchandiseSearchFormResultItem
              key={brand.id}
              itemId={brand.id}
              type="brand"
              index={index}
            />
          ))}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
  </Group>
}

export default MerchandiseSearchFormNoResults
